import { WIDGET_KINDS } from "@playbookai/types";
const NO_FIELDS = [];
/** Presentation-only dashboard kinds whose params are an empty object. */
function readOnlyDashboard(kind, reason) {
    return { kind, disposition: "read_only", reason, fields: NO_FIELDS };
}
/** Dashboard-instance kinds with a small presentation-config surface. */
function presentation(kind, fields, reason, valueSets = NO_FIELDS) {
    return {
        kind,
        disposition: "presentation_only",
        reason,
        fields,
        valueSets,
    };
}
const ENTRIES = [
    // ---------------------------------------------------------------- Specify
    {
        kind: "problemStatement",
        disposition: "operations",
        fields: ["problem"],
    },
    {
        kind: "tags",
        disposition: "operations",
        fields: ["metric", "trend", "actual", "gap", "standard"],
        // The plan: "Schema version is system metadata, not an editable field."
        systemFields: ["schemaVersion"],
    },
    {
        kind: "runChart",
        disposition: "operations",
        fields: ["title", "unit", "target"],
        collections: [
            {
                collection: "points",
                /* A minted `id`, not the period label. Label-as-identity was tried and
                   is wrong twice over: renaming a point would read as a delete plus an
                   add, losing a concurrent edit to that point's value; and because a
                   removal leaves a tombstone under its key, deleting "Wk 1" would
                   permanently block ever creating a point called "Wk 1" again. With an
                   id, a rename is an ordinary field edit and `label` stays editable. */
                itemIdField: "id",
                fields: ["label", "value"],
                maxItems: 500,
            },
        ],
    },
    {
        kind: "kpiTile",
        disposition: "operations",
        // The BINDING and its presentation, and nothing else.
        //
        // `value`, `unit`, `target`, `higherIsBetter`, `sourceText` and the
        // `recentValues` series are gone from this list because they are gone from
        // the widget: KPI Manager owns them and serves them at render time. An AI
        // proposal can point the tile at a KPI the requester can already read, or
        // relabel it, or change which window it shows — it has no path to a
        // measurement or a target, and the executor refuses one because there is no
        // spec to resolve. Changing a governed number stays KPI Manager's own
        // authorized, audited workflow.
        //
        fields: ["kpiId", "label", "range"],
        // `legacy` is a frozen snapshot of what a pre-binding tile used to say,
        // kept so the migration destroys nothing. It is migration metadata, not
        // content: nobody — person or model — edits it in place, and choosing a KPI
        // is what retires it.
        systemFields: ["legacy"],
    },
    {
        kind: "problemTree",
        disposition: "operations",
        fields: ["problem"],
        collections: [
            {
                collection: "effects",
                itemIdField: "id",
                fields: ["text"],
                maxItems: 16,
            },
            {
                collection: "causes",
                itemIdField: "id",
                fields: ["text", "status"],
                maxItems: 16,
            },
        ],
    },
    // ---------------------------------------------------------------- Oppress
    {
        kind: "currentStateStats",
        disposition: "operations",
        fields: NO_FIELDS,
        collections: [
            {
                collection: "stats",
                itemIdField: "id",
                fields: ["label", "value", "delta", "tone"],
                maxItems: 50,
            },
        ],
    },
    {
        kind: "containmentCards",
        disposition: "operations",
        fields: NO_FIELDS,
        collections: [
            {
                collection: "actions",
                itemIdField: "id",
                fields: [
                    "title",
                    "detail",
                    "owner",
                    "due",
                    "status",
                    "percentComplete",
                ],
                maxItems: 100,
            },
        ],
    },
    {
        kind: "problemTimeline",
        disposition: "operations",
        fields: [
            "exposureRatePerDay",
            "estimatedExposure",
            "exposureBasis",
            "exposureUnit",
        ],
        collections: [
            {
                collection: "milestones",
                itemIdField: "id",
                // `date` and `endDate` are a pair: the plan requires them validated
                // together, which callers express by putting both set_field operations
                // in one atomic group.
                fields: ["kind", "label", "date", "endDate", "estimated", "projected"],
                maxItems: 100,
            },
        ],
    },
    {
        kind: "containmentPlan",
        disposition: "operations",
        fields: ["revisitCadenceDays", "nextReviewDate"],
        collections: [
            {
                collection: "actions",
                itemIdField: "id",
                fields: [
                    "action",
                    "owner",
                    "ownerUserId",
                    "eta",
                    "priority",
                    "status",
                    "effectiveness",
                    "protectedScope",
                    "startedOn",
                    "verificationMethod",
                    "verificationCadence",
                    "successCriteria",
                    "effectivenessEvidence",
                    "sideEffectSafeguard",
                    "escalationTrigger",
                    // A cross-WIDGET reference into problemTimeline.milestones[].id with
                    // no referential check in either schema. Editable, but deleting the
                    // referenced milestone cannot be seen from here — see the P1 design
                    // note on dangling cross-widget references.
                    "milestoneId",
                    "jiraKey",
                ],
                maxItems: 100,
                children: [
                    {
                        collection: "notes",
                        itemIdField: "id",
                        fields: ["author", "at", "text"],
                        maxItems: 500,
                    },
                ],
            },
        ],
    },
    {
        kind: "exposureRiskReview",
        disposition: "operations",
        fields: NO_FIELDS,
        collections: [
            {
                collection: "risks",
                itemIdField: "id",
                fields: [
                    "title",
                    "type",
                    "category",
                    "description",
                    "affectedScope",
                    "basis",
                    "criticality",
                ],
                /* `risks[].assumptions` is `MediumText[]` (exposureRiskReview.ts:46) —
                   bare strings, so an ASSUMPTION has no identity and can never be a
                   target of its own. The RISK does have one, so the list is addressable
                   as a single value on `risks[<id>]/assumptions`: adding, rewording or
                   dropping an assumption is one atomic replacement of that one risk's
                   list, fenced by that one target's revision.
        
                   It was previously declared as unaddressable, and the whole cost of
                   that landed on staging `0bc92b75`: a background review generated
                   `record-no-fallback-assumption`, preflight answered
                   `unsupported_target at risks[q4-coverage-options-fail]/assumptions`,
                   and the job settled `VALIDATION_FAILED` with the bundle unapplied. */
                itemValueSets: ["assumptions"],
                maxItems: 50,
            },
        ],
    },
    // ------------------------------------------------------------------- Look
    {
        kind: "paretoL1",
        disposition: "operations",
        fields: ["title", "unit"],
        collections: [
            {
                collection: "bars",
                itemIdField: "id",
                fields: ["label", "value"],
                maxItems: 50,
            },
        ],
    },
    {
        kind: "paretoL2",
        disposition: "operations",
        fields: ["title", "unit"],
        collections: [
            {
                collection: "bars",
                itemIdField: "id",
                fields: ["label", "value"],
                maxItems: 50,
            },
        ],
    },
    {
        kind: "funnel",
        disposition: "operations",
        fields: ["title"],
        collections: [
            {
                collection: "levels",
                itemIdField: "id",
                fields: ["title"],
                // `.length(3)` — the three-level constraint the plan requires keeping.
                fixedLength: 3,
                children: [
                    {
                        collection: "items",
                        itemIdField: "id",
                        fields: ["text"],
                        maxItems: 50,
                    },
                ],
            },
        ],
    },
    {
        kind: "causalChain",
        disposition: "operations",
        fields: ["problem"],
        collections: [
            {
                collection: "steps",
                /* A minted `id`. There was never a derivable identity here: the only
                   candidate is `label`, an ORDINAL ("Why 3") that renumbers when a step
                   is inserted — array position wearing a different name.
        
                   `isRoot` stays editable and stays unconstrained: two concurrent
                   "mark as root" edits still produce two roots, because no schema
                   invariant forbids it. That is a pre-existing domain gap, not one this
                   addressing scheme introduces, and narrowing it would reject content
                   that already exists. */
                itemIdField: "id",
                fields: ["label", "body", "isRoot"],
                maxItems: 20,
            },
        ],
    },
    {
        kind: "fishbone",
        disposition: "operations",
        fields: ["effect"],
        collections: [
            {
                collection: "branches",
                itemIdField: "id",
                fields: ["category"],
                maxItems: 20,
                children: [
                    {
                        collection: "causes",
                        itemIdField: "id",
                        fields: ["text"],
                        maxItems: 50,
                    },
                ],
            },
        ],
    },
    {
        kind: "processTrace",
        disposition: "operations",
        fields: ["subject", "conclusion"],
        groups: [
            {
                group: "por",
                fields: ["observation", "location", "evidence", "status"],
                // superRefine: status === "validated" requires non-blank evidence.
                linkedFields: [["status", "evidence"]],
            },
            {
                group: "poo",
                fields: [
                    "location",
                    "expected",
                    "actual",
                    "divergence",
                    "evidence",
                    "status",
                ],
                linkedFields: [["status", "evidence"]],
            },
            {
                group: "poc",
                fields: ["cause", "location", "validationMethod", "evidence", "status"],
                // validated additionally requires a validation method.
                linkedFields: [["status", "evidence", "validationMethod"]],
            },
        ],
    },
    // ----------------------------------------------------------------- Verify
    {
        kind: "impactEffort",
        disposition: "operations",
        fields: NO_FIELDS,
        collections: [
            {
                collection: "points",
                itemIdField: "id",
                // One drag changes effort and impact together; callers submit both
                // set_field operations in one atomic group so a half-move is
                // unrepresentable.
                fields: ["label", "effort", "impact"],
                maxItems: 500,
            },
        ],
        // Exclusion membership: a set of FOREIGN ids, not owned items. Still one
        // unit for a `set_field` — a model-authored replacement for the whole list
        // would silently un-park everything somebody else parked — but addressable
        // per MEMBER through `exclude_item` / `include_item`, which is what Park
        // idea and Restore to matrix are.
        valueSets: ["excludedIds"],
        exclusion: { valueSet: "excludedIds", collection: "points" },
    },
    {
        kind: "countermeasureTable",
        disposition: "operations",
        fields: NO_FIELDS,
        collections: [
            {
                collection: "rows",
                itemIdField: "id",
                fields: [
                    "title",
                    "owner",
                    "ownerUserId",
                    "impact",
                    "effort",
                    "window",
                    "status",
                ],
                maxItems: 100,
            },
        ],
    },
    {
        kind: "stakeholderMap",
        disposition: "operations",
        fields: NO_FIELDS,
        collections: [
            {
                collection: "stakeholders",
                itemIdField: "id",
                fields: ["name", "userId", "role", "quadrant", "note"],
                maxItems: 100,
            },
        ],
    },
    // ----------------------------------------------------------------- Ensure
    {
        kind: "controlPlan",
        disposition: "operations",
        fields: NO_FIELDS,
        collections: [
            {
                collection: "rows",
                itemIdField: "id",
                fields: [
                    "item",
                    "cadence",
                    "responsible",
                    "responsibleUserId",
                    "trigger",
                ],
                maxItems: 50,
            },
        ],
    },
    {
        kind: "escalationMatrix",
        disposition: "operations",
        fields: NO_FIELDS,
        collections: [
            {
                collection: "cells",
                /* A minted `id`, with `(severity, level)` as ORDINARY CONTENT that the
                   kind's schema now requires to be unique. The plan asks for both
                   things — "composite identity must be unique" and "coordinate changes
                   are a move, not a new arbitrary array index" — and an id delivers
                   them better than a derived key would: moving a cell is a `set_field`
                   on `level` that keeps the cell's identity and its revision, and
                   clearing a cell then filling that coordinate again works, which a
                   coordinate-keyed tombstone would have blocked for ever. */
                itemIdField: "id",
                fields: ["severity", "level", "label", "tone"],
                maxItems: 9,
            },
        ],
    },
    // -------------------------------------------------- Phase-agnostic SOLVE
    {
        kind: "genericTable",
        disposition: "operations",
        fields: ["title", "description"],
        systemFields: ["schemaVersion", "binding"],
        collections: [
            {
                collection: "columns",
                itemIdField: "id",
                // A column's `type` and `options` are cross-validated against every
                // cell that references them, so a type change is a structural group.
                fields: ["label", "type", "required", "width", "unit", "origin"],
                maxItems: 40,
                children: [
                    {
                        collection: "options",
                        itemIdField: "id",
                        fields: ["label"],
                        maxItems: 100,
                    },
                ],
            },
            {
                collection: "rows",
                itemIdField: "id",
                fields: ["sourceKey", "origin"],
                maxItems: 500,
                records: [
                    {
                        record: "cells",
                        keySource: "genericTable.columns[].id — a cell is addressed by (rowId, columnId), which is what makes two people editing two cells of one row independent (AC1).",
                    },
                ],
            },
        ],
    },
    {
        kind: "markdownText",
        disposition: "operations",
        // Title and body are separate units; the body is one last-save-wins field
        // (the plan is explicit that no character merge is required here).
        fields: ["title", "markdown"],
        systemFields: ["schemaVersion"],
    },
    // ------------------------------------------ Shared entity reference cards
    {
        kind: "imageTile",
        disposition: "operations",
        fields: ["url", "alt"],
    },
    {
        kind: "linkTile",
        disposition: "operations",
        fields: ["label", "href"],
    },
    // -------------------------------------------------------------------- VSM
    //
    // CORRECTED (PB-4923 P5). These four were dispositioned `external_store` on
    // the stated grounds that VSM keeps their content on the feature-owned Yjs
    // key `vsmArtifacts`. That is not where it lives. `VsmArtifactsService.save`
    // writes `widget.params` on the row keyed `(refName: 'vsm', refId: vsmId,
    // artifactKey)`, the client calls it through `vsmArtifactsApi.save`, and
    // `readArtifactParams(doc, …)` is a LEGACY fallback read used only when no
    // row exists yet — `VsmArtifactWidgetBody` names that local `legacy`.
    //
    // The consequence of the wrong reason was the wrong conclusion: those saves
    // are WHOLE-artifact with an `expectedVersion` CAS, so two people editing two
    // different SIPOC cells produce a 409 for the second and "re-apply your
    // change if needed" — the exact lost-update PB-4923 exists to remove. What
    // rightly kept them off the SOLVE command path is `NOT_MODULE_OWNED`, which
    // is ownership enforcement and stays; the operations they now accept run
    // through VSM's OWN service, gates and audit.
    //
    // What stays `external_store` stays for a schema reason, stated per entry.
    {
        kind: "sipoc",
        disposition: "operations",
        fields: NO_FIELDS,
        collections: [
            {
                collection: "rows",
                itemIdField: "id",
                ordered: true,
                fields: ["process", "linkedEntityId"],
                maxItems: 50,
                children: [
                    {
                        collection: "suppliers",
                        itemIdField: "id",
                        ordered: true,
                        fields: ["text", "linkedEntityId", "classification"],
                        maxItems: 50,
                    },
                    {
                        collection: "inputs",
                        itemIdField: "id",
                        ordered: true,
                        fields: ["text", "linkedEntityId", "requirements", "notes"],
                        maxItems: 50,
                    },
                    {
                        collection: "outputs",
                        itemIdField: "id",
                        ordered: true,
                        fields: ["text", "linkedEntityId", "requirements", "notes"],
                        maxItems: 50,
                    },
                    {
                        collection: "customers",
                        itemIdField: "id",
                        ordered: true,
                        fields: ["text", "linkedEntityId", "classification"],
                        maxItems: 50,
                    },
                ],
            },
        ],
    },
    {
        kind: "goals",
        disposition: "external_store",
        reason: "Not an artifact of its own: `goals` is embedded INSIDE the VSM charter aggregate (`VSM_ARTIFACT_KEYS` has no `goals` key), so it has no widget row to address and no placement surface accepts it. Its units are mapped so the charter can adopt them if the charter itself ever becomes addressable.",
        fields: NO_FIELDS,
        collections: [
            {
                collection: "rows",
                itemIdField: "id",
                fields: ["metric", "baseline", "target", "unit"],
                maxItems: 50,
            },
        ],
    },
    {
        kind: "charter",
        disposition: "external_store",
        reason: "The STORED form is not the validated form: `StoredCharterParamsSchema` accepts a v2 `tags.schemaVersion` and a free-text `trend` and normalises both on read, while the executor validates a merged candidate against `CharterParamsSchema`, which rejects them. An operation against a legacy row would answer `invalid` for a field nobody touched, so the charter needs a content upgrade before it can be addressed leaf by leaf.",
        fields: NO_FIELDS,
    },
    {
        /* Also CORRECTED. The old reason — a `.transform()` deriving `label` and
           `filled` makes a merged candidate "not byte-stable under re-validation" —
           does not hold against how the executor actually commits: it keeps
           `validation.data`, not its own candidate, so a derived field is
           RECOMPUTED from the merged `body` on every group rather than carried
           forward stale. The transform is idempotent (`label` from the enum `key`,
           `filled` from `body.trim()`), and neither derived field is writable here
           — `outcomes` maps `body` alone. */
        kind: "projectContext",
        disposition: "operations",
        fields: NO_FIELDS,
        collections: [
            {
                collection: "stakeholders",
                itemIdField: "id",
                fields: ["role", "name"],
                maxItems: 100,
            },
            {
                collection: "outcomes",
                itemIdField: "key",
                fields: ["body"],
                fixedLength: 4,
            },
        ],
    },
    // -------------------------------------------------------------- Dashboard
    presentation("singleKpi", ["kpiId", "chartType", "showDelta", "showCommentary"], "Dashboard-instance presentation config (ADR-0054); one owner, no shared-canvas concurrency and no AI write path."),
    presentation("chart", ["kpiId", "chartType", "showDelta", "showCommentary"], "Dashboard-instance presentation config (ADR-0054); retired for new instances."),
    presentation("kpiWatchlist", NO_FIELDS, "Dashboard-instance selection of foreign KPI ids; the dashboard contract narrows params to {} entirely.", ["kpiIds"]),
    presentation("kpiTable", NO_FIELDS, "Dashboard-instance selection of foreign KPI ids; the dashboard contract narrows params to {} entirely.", ["kpiIds"]),
    presentation("myTasks", ["mode"], "Dashboard-instance presentation config; `priorities` is an enum filter set with no item identity.", ["priorities"]),
    readOnlyDashboard("projectPulse", "Params are an empty object; the body is entirely derived from a dataset."),
    readOnlyDashboard("strategicHealth", "Dashboard-derived body. `stats` is generated, carries no ids and is NOT_CUSTOMIZABLE on the dashboard contract, so it has no editing control to map."),
    readOnlyDashboard("dailyBrief", "Dashboard-derived body. `highlights` is generated and carries no ids."),
    readOnlyDashboard("coachingNudge", "Dashboard-derived body; nudges are produced by the dataset, not authored."),
    readOnlyDashboard("moodCheck", "Params are an empty object."),
    readOnlyDashboard("mySolves", "Params are an empty object."),
    readOnlyDashboard("myVsms", "Params are an empty object."),
    presentation("quickAccess", ["mode"], "Dashboard-instance navigation preference; `selectedModuleIds` is a unique-enforced value set replaced as one unit.", ["selectedModuleIds"]),
    readOnlyDashboard("kaizenTable", "Params are an empty object; filters live in the dataset input, not in params."),
    readOnlyDashboard("kaizenHealth", "Params are an empty object."),
    presentation("solveActions", ["groupBy"], "Dashboard-instance presentation only; the source, SOLVE, owner, status and due filters live in the dataset input, and the rows themselves are a read model with no write path from Home.", ["columns"]),
    // -------------------------------------------- Hoshin/VSM Yjs-native cards
    {
        kind: "textCard",
        disposition: "external_store",
        reason: "Yjs-native canvas card (WIDGET_STORAGE 'yjs'): its content lives inline in the canvas Y.Doc, where the CRDT already merges concurrent edits. ADR-0020 keeps that boundary and AC3 requires not downgrading it.",
        fields: NO_FIELDS,
    },
    {
        kind: "image",
        disposition: "external_store",
        reason: "Yjs-native canvas card; content lives inline in the canvas Y.Doc.",
        fields: NO_FIELDS,
    },
    {
        kind: "linkCard",
        disposition: "external_store",
        reason: "Yjs-native canvas card; content lives inline in the canvas Y.Doc.",
        fields: NO_FIELDS,
    },
    {
        kind: "kpiCard",
        disposition: "external_store",
        reason: "Yjs-native canvas card carrying its own typed metric (KpiCardParamsSchema), so its `recentValues` identity gap exists in the Y.Doc too; the CRDT owns merging there.",
        fields: NO_FIELDS,
    },
    // ------------------------------------------------------------ LAMA report
    readOnlyDashboard("lamaReportOverview", "LAMA report section (ADR-0054); params are empty and the body is derived from the campaign report dataset."),
    readOnlyDashboard("lamaMaturityProfile", "LAMA report section; params are empty and the body is dataset-derived."),
    presentation("lamaMaturityTrend", ["waveWindow"], "LAMA report section with one presentation parameter; dataset-derived body."),
    presentation("lamaParticipationTrend", ["waveWindow"], "LAMA report section with one presentation parameter; dataset-derived body."),
    readOnlyDashboard("lamaCategoryResults", "LAMA report section; params are empty and the body is dataset-derived."),
    readOnlyDashboard("lamaPillarResults", "Pre-rename alias of lamaCategoryResults, retained resolvable; params are empty."),
    readOnlyDashboard("lamaMaturityHierarchy", "LAMA maturity hierarchy report; params are empty and the body is derived from baseline and response datasets."),
    readOnlyDashboard("lamaHierarchyResults", "LAMA pillar, category and question results; params are empty and the body is derived from baseline and response datasets."),
    readOnlyDashboard("lamaTeamHeatmap", "LAMA report section; params are empty and the body is dataset-derived."),
    presentation("lamaTopMovers", ["limit"], "LAMA report section with one presentation parameter; dataset-derived body."),
    presentation("lamaPriorityRoadmap", ["limit"], "LAMA report section with one presentation parameter; dataset-derived body."),
];
/**
 * Every item-level value set in the register, as `"<kind>/<collection path>"`.
 *
 * DERIVED from the collection specs rather than hand-listed beside them: the
 * completeness spec uses this to tell "mapped as a value set" from "forgotten",
 * and a second hand-maintained list is how the two answers drift. A nested array
 * on an item is either declared on its own spec or it is a finding.
 */
export const WIDGET_ITEM_VALUE_SETS = Object.freeze(Object.fromEntries(ENTRIES.flatMap((entry) => {
    const pairs = [];
    walkCollections(entry.collections ?? [], "", (spec, path) => {
        if (spec.itemValueSets === undefined)
            return;
        if (spec.itemValueSets.length === 0)
            return;
        pairs.push([`${entry.kind}/${path}`, spec.itemValueSets]);
    });
    return pairs;
})));
export const WIDGET_MERGE_MAP = Object.freeze(Object.fromEntries(ENTRIES.map((entry) => [entry.kind, entry])));
export function getWidgetMergeMap(kind) {
    const entry = WIDGET_MERGE_MAP[kind];
    if (entry === undefined) {
        throw new Error(`No merge map registered for widget kind '${kind}'.`);
    }
    return entry;
}
/** Kinds whose content this executor may merge. */
export const OPERATION_MERGED_WIDGET_KINDS = WIDGET_KINDS.filter((kind) => WIDGET_MERGE_MAP[kind]?.disposition === "operations");
/** True when this collection, or any collection beneath it, is opaque-bodied. */
function anyOpaque(specs) {
    return specs.some((spec) => spec.opaqueItemBody === true || anyOpaque(spec.children ?? []));
}
/**
 * Kinds holding at least one `opaqueItemBody` collection — the kinds whose
 * `edit_fields` action may offer `replace_item`.
 *
 * DERIVED, never hand-listed: the AI catalogue and the executor must agree
 * about which kinds accept the operation, and a second list is how they drift.
 * Publishing `replace_item` for a kind the executor refuses would advertise a
 * capability that answers `unsupported_target`.
 */
export const OPAQUE_ITEM_WIDGET_KINDS = WIDGET_KINDS.filter((kind) => {
    const entry = WIDGET_MERGE_MAP[kind];
    if (entry === undefined || entry.disposition !== "operations")
        return false;
    return anyOpaque(entry.collections ?? []);
});
/**
 * Kinds whose `edit_fields` action may offer `exclude_item` / `include_item`.
 *
 * DERIVED, for the same reason `OPAQUE_ITEM_WIDGET_KINDS` is: the catalogue and
 * the executor have to agree about which kinds accept the operation, and a
 * second hand-kept list is how they drift. Publishing it for a kind with no
 * declared exclusion would advertise an operation answered
 * `unsupported_target`.
 */
export const EXCLUSION_WIDGET_KINDS = WIDGET_KINDS.filter((kind) => {
    const entry = WIDGET_MERGE_MAP[kind];
    if (entry === undefined || entry.disposition !== "operations")
        return false;
    return entry.exclusion !== undefined;
});
/**
 * Every collection in the register that has no stable item identity today,
 * as `"<kind>/<path>"`. This is the P1 identity-gap register in machine-readable
 * form: the executor consults it to refuse item operations, and P4 closes the
 * entries one at a time.
 */
export function widgetIdentityGaps() {
    const gaps = [];
    for (const entry of ENTRIES) {
        walkCollections(entry.collections ?? [], "", (spec, path) => {
            if (spec.itemIdField !== null)
                return;
            gaps.push({
                kind: entry.kind,
                path,
                reason: spec.identityGap ?? "no stable item identity",
            });
        });
    }
    return gaps;
}
/**
 * The stable identity of one item, or `null` when it has none.
 *
 * `null` is the whole answer for "this operation cannot be addressed": the
 * collection declares no id field, the item is not an object, or its id is
 * missing or empty. Every caller treats it the same way — refuse the
 * operation, never fall back to array position.
 *
 * A MISSING id is expected, not exceptional: PB-4923 added `id` to
 * `causalChain.steps`, `runChart.points` and `escalationMatrix.cells` as an
 * OPTIONAL property so rows written before the backfill migration still
 * validate. Such an item simply cannot be addressed until it has one, and the
 * diff falls the whole collection back to replacement — the pre-PB-4923
 * behaviour, which is the right thing to degrade to.
 */
export function widgetItemIdentity(spec, item) {
    const field = spec.itemIdField;
    if (field === null)
        return null;
    if (typeof item !== "object" || item === null || Array.isArray(item)) {
        return null;
    }
    const value = item[field];
    return typeof value === "string" && value.length > 0 ? value : null;
}
/** Depth-first walk over a kind's collections, yielding each with its path. */
export function walkCollections(collections, prefix, visit) {
    for (const spec of collections) {
        const path = prefix === "" ? spec.collection : `${prefix}/${spec.collection}`;
        visit(spec, path);
        if (spec.children)
            walkCollections(spec.children, path, visit);
    }
}
//# sourceMappingURL=merge-map.js.map